import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import Seo, { breadcrumbSchema, serviceSchema, faqSchema } from "@/components/Seo";
import { Reveal, CallButton, RequestButton, Breadcrumbs, CtaBand } from "@/components/Blocks";
import { BUSINESS } from "@/data/site";
import { SERVICES } from "@/data/services";
import { COUNTIES, CITIES } from "@/data/locations";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ServicePage({ service: s }) {
    const crumbs = [
        { name: "Home", path: "/" },
        { name: "Services", path: "/#services" },
        { name: s.name, path: `/${s.slug}` },
    ];
    return (
        <div>
            <Seo
                title={s.metaTitle}
                description={s.metaDesc}
                path={`/${s.slug}`}
                schemas={[
                    breadcrumbSchema(crumbs),
                    serviceSchema(`${s.short} repair`),
                    faqSchema(s.faq),
                ]}
            />

            <section className="bg-slate-950 text-white dark-grain">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14 grid lg:grid-cols-2 gap-8 items-center">
                    <div>
                        <Breadcrumbs items={crumbs} />
                        <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-3">{s.kicker}</p>
                        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">{s.h1}</h1>
                        <p className="mt-4 text-slate-200 leading-relaxed">{s.intro[0]}</p>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <CallButton location={`service-${s.slug}`} testid="service-call-btn" />
                            <RequestButton testid="service-request-btn" />
                        </div>
                        <p className="mt-3.5 text-xs text-slate-400">$155 service call — waived with completed repair · 6-month warranty on completed repairs</p>
                    </div>
                    <Reveal delay={0.15} className="hidden lg:block">
                        <div className="rounded-3xl overflow-hidden shadow-2xl border border-slate-800 rotate-1">
                            <img src={s.image} alt={`${s.name} — in-home service in Brevard & Seminole County, FL`} className="w-full h-[320px] object-cover" width="1200" height="320" loading="eager" />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                <div className="grid lg:grid-cols-[1fr_320px] gap-10">
                    <div>
                        {s.intro.slice(1).map((p, i) => (
                            <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p>
                        ))}

                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-7 mb-5">
                            Common {s.short.toLowerCase()} problems we repair
                        </h2>
                        {s.problemsNote && <p className="text-slate-600 leading-relaxed mb-5 -mt-2">{s.problemsNote}</p>}
                        <div className="grid sm:grid-cols-2 gap-4" data-testid="service-problems">
                            {s.problems.map((p, i) => (
                                <Reveal key={p.t} delay={(i % 2) * 0.05}>
                                    <div className="rounded-xl border border-slate-200 bg-white p-5 h-full shadow-sm">
                                        <h3 className="font-heading font-bold text-slate-900">{p.t}</h3>
                                        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{p.d}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <div className="mt-7 rounded-xl border-l-4 border-amber-400 bg-amber-50 p-5 sm:p-6" data-testid="service-approach">
                            <p className="text-slate-700 leading-relaxed">{s.approach}</p>
                        </div>

                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-10 mb-5">
                            {s.short} repair questions
                        </h2>
                        <Accordion type="single" collapsible>
                            {s.faq.map((f, i) => (
                                <AccordionItem key={i} value={`sfaq-${i}`}>
                                    <AccordionTrigger className="text-left font-heading font-semibold text-slate-900" data-testid={`service-faq-${i}`}>{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>

                    <aside className="space-y-6">
                        <Reveal>
                            <div className="rounded-2xl bg-slate-950 text-white p-6 sticky top-28" data-testid="service-sidebar">
                                <div className="flex items-center gap-2 text-amber-400">
                                    <ShieldCheck className="h-5 w-5" />
                                    <span className="font-heading font-bold text-sm">6-Month Warranty</span>
                                </div>
                                <p className="mt-2 text-sm text-slate-200">Every completed repair includes our 6-month warranty.</p>
                                <div className="my-4 border-t border-slate-800" />
                                <p className="font-heading text-2xl font-extrabold">$155 Service Call</p>
                                <p className="text-sm text-slate-200 mt-1">Waived with completed repair.</p>
                                <CallButton location={`service-sidebar-${s.slug}`} className="w-full mt-4" testid="sidebar-call-btn" />
                                <RequestButton className="w-full mt-3" testid="sidebar-request-btn" />
                                <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                                    After-hours emergency service may be available 10 PM – 6 AM. Call {BUSINESS.phone} for availability.
                                </p>
                            </div>
                        </Reveal>
                    </aside>
                </div>
            </section>

            <section className="bg-slate-100/70 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-12">
                    <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">More repair services</h2>
                    <div className="flex flex-wrap gap-2.5">
                        {SERVICES.filter((x) => x.slug !== s.slug).map((x) => (
                            <Link key={x.slug} to={`/${x.slug}`}
                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-red-500 hover:text-red-600 transition-all"
                                data-testid={`related-service-${x.slug}`}>
                                {x.name} <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                        ))}
                    </div>
                    <h2 className="font-heading text-xl font-bold text-slate-900 mt-8 mb-4">Where we repair {s.short.toLowerCase()}s</h2>
                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                        {COUNTIES.map((c) => (
                            <Link key={c.slug} to={`/${c.slug}`} className="font-medium text-slate-600 hover:text-red-600">{s.short} repair in {c.name}</Link>
                        ))}
                        {CITIES.map((c) => (
                            <Link key={c.slug} to={`/${c.slug}`} className="font-medium text-slate-600 hover:text-red-600">{s.short} repair in {c.name}, FL</Link>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand
                title={<>Need {s.short.toLowerCase()} repair? <span className="text-amber-400">Call Appliance Mania.</span></>}
                sub={`In-home ${s.short.toLowerCase()} repair across Brevard & Seminole Counties — ${BUSINESS.phone}.`}
                testid={`service-cta-${s.slug}`}
            />
        </div>
    );
}
